import { useState } from 'react';
import {
  Modal,
  Button,
  Stack,
  Text,
  Group,
  Stepper,
  PinInput,
  Paper,
  Alert,
  Divider,
  ActionIcon,
  CopyButton,
  Tooltip,
} from '@mantine/core';
import { IconShield, IconDownload, IconCheck, IconCopy, IconAlertCircle } from '@tabler/icons-react';
import QRCode from 'react-qr-code';
import { useTheme } from '@contexts/ThemeContext';
import { loginGlassModal } from '@components/login-background/loginGlassModal';
import '@components/login-background/login-form-glass.css';

interface MFASetupModalProps {
  opened: boolean;
  qrCodeUrl: string;
  secret: string;
  backupCodes: string[];
  deviceId: number;
  onVerify: (code: string) => Promise<void>;
  onClose: () => void;
  isVerifying?: boolean;
}

export const MFASetupModal = ({
  opened,
  qrCodeUrl,
  secret,
  backupCodes,
  deviceId,
  onVerify,
  onClose,
  isVerifying = false,
}: MFASetupModalProps) => {
  const { mode, colors: themeColors } = useTheme();
  const glassModal = loginGlassModal(mode);
  const [active, setActive] = useState(0);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodesSaved, setBackupCodesSaved] = useState(false);

  const handleDownloadBackupCodes = () => {
    const content = `JARVIS MFA BACKUP CODES
=========================

PENTING: Simpan kode-kode ini di tempat yang aman!
Kode backup ini dapat digunakan untuk login jika Anda kehilangan akses ke perangkat autentikasi.

${backupCodes.map((code, idx) => `${idx + 1}. ${code}`).join('\n')}

Tanggal Download: ${new Date().toLocaleString('id-ID')}
Device ID: ${deviceId}

=========================
JANGAN BAGIKAN KODE INI KEPADA SIAPAPUN
`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `jarvis-backup-codes-${new Date().getTime()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    setBackupCodesSaved(true);
  };

  const handleVerify = async () => {
    if (verificationCode.length === 6) {
      await onVerify(verificationCode);
    }
  };

  const nextStep = () => setActive((current) => (current < 2 ? current + 1 : current));
  const prevStep = () => setActive((current) => (current > 0 ? current - 1 : current));

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      centered
      size="lg"
      zIndex={10000}
      overlayProps={glassModal.overlayProps}
      styles={glassModal.styles}
    >
      {/* Opens over the login page, so it wears the login's glass. */}
      <div className="login-glass" data-login-surface={glassModal.surface}>
        <Stack gap="lg" py="md">
          {/* Header */}
          <Group justify="center">
            <IconShield size={50} style={{ color: themeColors.primary }} />
          </Group>
          <Text size="xl" fw={700} ta="center" style={{ color: themeColors.text }}>
            Setup Autentikasi Multi-Faktor (MFA)
          </Text>

          {/* Stepper */}
          <Stepper active={active} onStepClick={setActive} allowNextStepsSelect={false}>
            {/* Step 1: QR Code */}
            <Stepper.Step label="Scan QR Code" description="Gunakan Google Authenticator">
              <Stack gap="md" mt="lg">
                <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light">
                  <Text size="sm">
                    Buka aplikasi <strong>Google Authenticator</strong> di smartphone Anda dan scan QR code di bawah
                    ini.
                  </Text>
                </Alert>

                {/* QR Code */}
                <Paper
                  withBorder
                  p="xl"
                  radius="md"
                  className="flex flex-col items-center"
                  style={{
                    backgroundColor: themeColors.formFields,
                    borderColor: themeColors.border,
                  }}
                >
                  {/* White background container for QR code to ensure scannability */}
                  <div
                    style={{
                      backgroundColor: '#FFFFFF',
                      padding: '16px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <QRCode value={qrCodeUrl} size={200} level="H" />
                  </div>

                  <Divider
                    my="md"
                    w="100%"
                    label="Atau masukkan kode manual"
                    labelPosition="center"
                    styles={{
                      label: { color: themeColors.textSecondary },
                    }}
                  />

                  <Group gap="xs" align="center" w="100%" justify="center">
                    <Text size="sm" fw={500} className="font-mono" style={{ color: themeColors.text }}>
                      {secret}
                    </Text>
                    <CopyButton value={secret}>
                      {({ copied, copy }) => (
                        <Tooltip label={copied ? 'Tersalin!' : 'Salin kode'}>
                          <ActionIcon color={copied ? 'teal' : 'blue'} variant="subtle" onClick={copy}>
                            {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Group>
                </Paper>

                <Group justify="flex-end" mt="md">
                  <Button onClick={nextStep}>Lanjut ke Backup Codes</Button>
                </Group>
              </Stack>
            </Stepper.Step>

            {/* Step 2: Backup Codes */}
            <Stepper.Step label="Backup Codes" description="Simpan kode darurat">
              <Stack gap="md" mt="lg">
                <Alert icon={<IconAlertCircle size={16} />} color="orange" variant="light">
                  <Text size="sm">
                    <strong>PENTING:</strong> Simpan kode backup ini di tempat yang aman. Anda akan membutuhkannya jika
                    kehilangan akses ke perangkat autentikasi.
                  </Text>
                </Alert>

                <Paper
                  withBorder
                  p="md"
                  radius="md"
                  style={{
                    backgroundColor: themeColors.formFields,
                    borderColor: themeColors.border,
                  }}
                >
                  <Stack gap="xs">
                    <Text size="sm" fw={600} style={{ color: themeColors.text }}>
                      Kode Backup Anda:
                    </Text>
                    <div className="grid grid-cols-2 gap-2">
                      {backupCodes.map((code, idx) => (
                        <Paper
                          key={code}
                          p="xs"
                          style={{
                            // mode === 'dark' only matched the one literal theme, not the
                            // other eleven dark ones, so this fell to the light fill.
                            backgroundColor: glassModal.isDark ? 'rgba(148, 163, 184, 0.12)' : '#F1F5F9',
                            border: `1px solid ${glassModal.isDark ? 'rgba(255,255,255,0.10)' : 'rgba(15,42,68,0.12)'}`,
                          }}
                        >
                          <Text size="sm" className="font-mono text-center" style={{ color: glassModal.text }}>
                            {idx + 1}. {code}
                          </Text>
                        </Paper>
                      ))}
                    </div>
                  </Stack>
                </Paper>

                <Button
                  leftSection={<IconDownload size={18} />}
                  onClick={handleDownloadBackupCodes}
                  variant="light"
                  color={backupCodesSaved ? 'teal' : 'blue'}
                  fullWidth
                >
                  {backupCodesSaved ? 'Backup Codes Telah Didownload' : 'Download Backup Codes'}
                </Button>

                <Group justify="space-between" mt="md">
                  <Button variant="subtle" onClick={prevStep}>
                    Kembali
                  </Button>
                  <Button onClick={nextStep} disabled={!backupCodesSaved}>
                    Lanjut ke Verifikasi
                  </Button>
                </Group>
              </Stack>
            </Stepper.Step>

            {/* Step 3: Verification */}
            <Stepper.Step label="Verifikasi" description="Masukkan 6 digit kode">
              <Stack gap="md" mt="lg">
                <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light">
                  <Text size="sm">Masukkan 6 digit kode yang muncul di aplikasi Google Authenticator Anda.</Text>
                </Alert>

                <Stack gap="md" align="center">
                  <PinInput
                    length={6}
                    type="number"
                    size="lg"
                    value={verificationCode}
                    onChange={setVerificationCode}
                    placeholder=""
                    oneTimeCode
                  />
                </Stack>

                <Group justify="space-between" mt="md">
                  <Button variant="subtle" onClick={prevStep}>
                    Kembali
                  </Button>
                  <Button onClick={handleVerify} disabled={verificationCode.length !== 6} loading={isVerifying}>
                    Verifikasi & Aktifkan MFA
                  </Button>
                </Group>
              </Stack>
            </Stepper.Step>
          </Stepper>
        </Stack>
      </div>
    </Modal>
  );
};

export default MFASetupModal;
